import { createApi } from '@reduxjs/toolkit/query/react';

import { UsersApi } from '.';
import { postData } from '../../services/API';
import {
  addPatientNoteUrl,
  removePatientNoteUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';

interface IAddPatientNotePayload {
  accountNumber: string;
  author: string;
  facilityId: string;
  message: string;
}

interface IRemovePatientNotePayload {
  accountNumber: string;
  facilityId: string;
  noteId: string;
}

/**
 * RTK Query API slice for patient features.
 *
 * @remarks
 * Provides endpoints for:
 * - Patient note management (`postAddPatientNote`, etc.)
 * - Patient management endpoints as defined
 *
 * @example
 * import { usePostAddPatientNoteMutation } from 'src/redux/apis/Patients.api';
 * const { data } = usePostAddPatientNoteMutation();
 */
export const patientsApi = createApi({
  reducerPath: 'patientsApi',
  baseQuery: (operation: any) => operation,
  tagTypes: ['patient-to-round'],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    postAddPatientNote: builder.mutation({
      query: (payload: IAddPatientNotePayload) =>
        postData(addPatientNoteUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          UsersApi.util.invalidateTags([
            {
              type: 'patient-to-round'
            }
          ])
        );
      }
    }),
    postRemovePatientNote: builder.mutation({
      query: (payload: IRemovePatientNotePayload) =>
        postData(removePatientNoteUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          UsersApi.util.invalidateTags([
            {
              type: 'patient-to-round'
            }
          ])
        );
      }
    })
  })
});

export const {
  usePostAddPatientNoteMutation,
  usePostRemovePatientNoteMutation
} = patientsApi;
